// ============================================================================
// VerifyEmailPage — route /account/verify-email?token=…  (namespace: "account")
// ----------------------------------------------------------------------------
// The landing page the trainer's verification email link opens (the backend
// mints {VF_PUBLIC_BASE_URL}/account/verify-email?token=…). It auto-POSTs the
// token to /api/account/email/verify/confirm on mount and renders the outcome:
//   • verifying  — a quiet spinner while the confirm round-trips
//   • verified    — success card + link back to the account page
//   • expired/used/invalid — a 400 maps to a clear "link expired" state with a
//     CTA back to the account page to send a fresh link
//   • missing token — defensive empty state (link malformed / token stripped)
//
// It lives behind RequireAuth (the confirm endpoint is trainer-scoped: the token
// must belong to THIS signed-in trainer), so an unauthenticated open redirects to
// login first. Built on the locked Pulse components; all copy via i18n.
// ============================================================================
import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";

import { Badge, Banner, Button, Card, CardHeader } from "../../components";
import { ApiError } from "../../api";
import { useConfirmEmailVerification } from "../../hooks";
import { FullPageSpinner } from "../../components/FullPageSpinner";

const EASE = [0.22, 0.61, 0.36, 1] as const;

export default function VerifyEmailPage() {
  const { t } = useTranslation("account");
  const reduce = useReducedMotion();
  const [params] = useSearchParams();
  const token = params.get("token");
  const confirm = useConfirmEmailVerification();

  // Fire the confirm exactly once for the token (StrictMode double-mount safe).
  const firedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!token) return;
    if (firedFor.current === token) return;
    firedFor.current = token;
    confirm.mutate(token);
  }, [token, confirm]);

  // No token in the URL — the link was malformed or the token stripped.
  if (!token) {
    return (
      <VerifyShell>
        <CardHeader
          title={t("verifyPage.missingTitle")}
          subtitle={t("verifyPage.missingSubtitle")}
        />
        <AccountCta label={t("verifyPage.backToAccount")} />
      </VerifyShell>
    );
  }

  // In flight (or before the effect fires) — quiet spinner.
  if (confirm.isIdle || confirm.isPending) {
    return <FullPageSpinner label={t("verifyPage.verifying")} />;
  }

  if (confirm.isError) {
    // A 400 = invalid / expired / already-used token or a subject mismatch.
    const expired =
      confirm.error instanceof ApiError && confirm.error.status === 400;
    return (
      <VerifyShell reduce={!!reduce}>
        <CardHeader
          title={expired ? t("verifyPage.expiredTitle") : t("verifyPage.errorTitle")}
          subtitle={
            expired
              ? t("verifyPage.expiredSubtitle")
              : t("verifyPage.errorSubtitle")
          }
        />
        <Banner tone={expired ? "warning" : "danger"}>
          {expired ? t("verifyPage.expiredBlurb") : t("verifyPage.errorBlurb")}
        </Banner>
        <AccountCta label={t("verifyPage.sendFresh")} />
      </VerifyShell>
    );
  }

  // Verified.
  return (
    <VerifyShell reduce={!!reduce}>
      <CardHeader
        title={t("verifyPage.verifiedTitle")}
        subtitle={t("verifyPage.verifiedSubtitle")}
        action={
          <Badge tone="success" dot>
            {t("email.verified")}
          </Badge>
        }
      />
      <Banner tone="success">{t("verifyPage.verifiedBlurb")}</Banner>
      <AccountCta label={t("verifyPage.backToAccount")} primary />
    </VerifyShell>
  );
}

function VerifyShell({
  children,
  reduce,
}: {
  children: React.ReactNode;
  reduce?: boolean;
}) {
  return (
    <div className="mx-auto flex min-h-screen max-w-lg items-center px-gutter py-12">
      <motion.div
        className="w-full"
        initial={reduce ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: EASE }}
      >
        <Card className="space-y-5">{children}</Card>
      </motion.div>
    </div>
  );
}

function AccountCta({ label, primary }: { label: string; primary?: boolean }) {
  return (
    <Link to="/app/account" className="block">
      <Button
        className="w-full sm:w-auto"
        variant={primary ? "primary" : "secondary"}
      >
        {label}
      </Button>
    </Link>
  );
}
